import React from "react";

import { Button } from "@/components/ui/button";
import { QtyInput } from "@/lib/scan-ui/qty-input";

/**
 * «Сколько в этом скане» у карточки последнего скана: со стеллажа берут
 * пачками, а скан даёт одну.
 *
 * ⚠️ Монтируется С `key={scanId}` и начальным значением ЭТОГО скана (с
 * сервера): состояние без ключа переживало новый скан, и на экране стояло
 * число прошлой позиции, а следующий ± слал «старое±1» в НОВЫЙ скан (PATCH
 * ставит абсолют). Жалоба склада 28.09.2026.
 *
 * ± и ввод с клавиатуры копятся ЛОКАЛЬНО; на сервер — одним PATCH по Enter,
 * «Готово» и при уходе карточки (новый скан, закрытие), если значение
 * изменилось. PATCH на каждый тап давал гонку при быстрых нажатиях.
 */
export function LastScanQty({
  initial,
  busy,
  onSave,
  onLeave,
  onDone,
}: {
  initial: number;
  busy: boolean;
  /** Сохранить сейчас; отказ — промис с ошибкой (её показывает страница). */
  onSave: (qty: number) => Promise<unknown>;
  /** Карточка уходит с несохранённым значением — сохранить, не дожидаясь. */
  onLeave: (qty: number) => void;
  /** Сохранено (или нечего сохранять) — закрыть карточку. */
  onDone: () => void;
}): React.ReactElement {
  const [draft, setDraft] = React.useState(initial);
  // Для очистки при размонтировании: замыкание эффекта видит только первый рендер.
  const state = React.useRef({ draft: initial, saved: initial, sending: null as number | null });
  state.current.draft = draft;

  const onLeaveRef = React.useRef(onLeave);
  onLeaveRef.current = onLeave;
  React.useEffect(
    () => () => {
      const s = state.current;
      if (s.draft !== s.saved && s.draft !== s.sending) onLeaveRef.current(s.draft);
    },
    [],
  );

  const save = async (): Promise<boolean> => {
    const s = state.current;
    const qty = s.draft;
    if (qty === s.saved) return true;
    s.sending = qty;
    try {
      await onSave(qty);
      s.saved = qty;
      return true;
    } catch {
      // Ошибку показывает страница (плашка); введённое остаётся на экране.
      return false;
    } finally {
      s.sending = null;
    }
  };

  const set = (n: number) => setDraft(Math.max(1, n));

  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="outline" onClick={() => set(draft - 1)} aria-label="Меньше">
        −
      </Button>
      <QtyInput value={draft} onChange={set} integer min={1} onEnter={() => void save()} />
      <Button size="sm" variant="outline" onClick={() => set(draft + 1)} aria-label="Больше">
        +
      </Button>
      <Button
        size="sm"
        disabled={busy}
        onClick={async () => {
          if (await save()) onDone();
        }}
      >
        Готово
      </Button>
    </div>
  );
}
